import { type FC, type ReactNode, useCallback, useRef, useState } from "react";
import type { AnySchema } from "yup";

import Tabs from "../../../../tabs/Tabs";
import UploadImage from "../UploadImage";
import { Modal } from "../../../../modal";
import NoResults from "../../../../noResults/NoResults";
import ImageUploaderModalButtons from "../ImageUploaderModalButtons/ImageUploaderModalButtons";

import fileUtils, { type OnUploadProgressHandler, type AcceptCancelationHandler } from "../../../../../utils/fileUtils";
import getSasConfig from "./sasConfig";
import ImageAspectRatio from "../../../../../enums/imageAspectRatio";

import "./imageUploaderModal.scss";
import { ModalSizes } from "../../../../../enums";

export interface ImageUploaderModalProps {
  value?: string | null;
  index?: string;
  trigger?: (openModal: () => void) => ReactNode;
  onImageChange?: (uri: string, fileName: string) => void;
  endpointToGetSasLink: string;
  isHidden?: boolean;
  getImageCustomValidator?: () => { validator: AnySchema; propName: string };
  aspectRatio?: ImageAspectRatio;
  showBestCrop?: boolean;
  onImageLoaded?: (file: ArrayBuffer | string | null) => void;
}

const ImageUploaderModal: FC<ImageUploaderModalProps> = ({
  value,
  index,
  trigger,
  onImageChange,
  endpointToGetSasLink,
  isHidden = true,
  aspectRatio = ImageAspectRatio.HD_16X9,
  getImageCustomValidator,
  showBestCrop,
  onImageLoaded,
}) => {
  const [croppedImage, setCroppedImage] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileName, setFileName] = useState<File[]>([]);

  const closeModalRef = useRef<(() => void) | null>(null);

  const handleSetSelectedFile = useCallback(
    (file: ArrayBuffer | string | null) => {
      setSelectedFile(typeof file === "string" ? file : null);
      onImageLoaded && onImageLoaded(file);
    },
    [onImageLoaded],
  );

  const handleSetFileName = useCallback((files: FileList) => {
    setFileName(Array.from(files));
  }, []);

  const resetUploadedFile = useCallback(() => {
    setSelectedFile(null);
    setFileName([]);
  }, []);

  const closeModalWindow = useCallback(
    (closeModal?: () => void) => {
      resetUploadedFile();
      (closeModal || closeModalRef.current)?.();
    },
    [resetUploadedFile],
  );

  const handleLooksGood = useCallback(() => {
    if (!croppedImage) return Promise.resolve();
    setIsUploading(true);
    const uploadOptions = {
      contentType: croppedImage.type,
    };

    const name = (fileName && fileName[0]?.name) || "image.jpg";
    const fileToUpload = new File([croppedImage], name, { type: croppedImage.type });

    const noopProgress: OnUploadProgressHandler = () => {};
    const noopAccept: AcceptCancelationHandler = () => {};

    return fileUtils
      .uploadFileToBlobStorage(
        fileToUpload,
        uploadOptions,
        noopProgress,
        noopAccept,
        getSasConfig(endpointToGetSasLink),
      )
      .then((result: { uri: string; fileName: string }) => {
        onImageChange && onImageChange(result.uri, result.fileName);
        setIsUploading(false);
        resetUploadedFile();
        closeModalRef.current && closeModalRef.current();
      })
      .catch(() => {
        setIsUploading(false);
      });
  }, [croppedImage, endpointToGetSasLink, fileName, onImageChange, resetUploadedFile]);

  const renderImageUploadSection = useCallback(() => {
    return (
      <Tabs.Pane label="Image Gallery" key={`${index}.uploadPane`}>
        <div className="image-upload-section">
          <NoResults description="coming soon" />
        </div>
      </Tabs.Pane>
    );
  }, [index]);

  const renderUploadPane = useCallback(() => {
    return (
      <Tabs.Pane label="Upload" key={`${index}.uploadPane`}>
        <div className="image-upload-section upload-image">
          <UploadImage
            onCroppedImageChange={setCroppedImage}
            file={(selectedFile as string) || (value as string)}
            onFileChanged={handleSetSelectedFile}
            fileName={fileName}
            onFileNameChanged={handleSetFileName}
            aspectRatio={aspectRatio}
            getImageCustomValidator={getImageCustomValidator}
            showBestCrop={!!showBestCrop}
          />
        </div>
      </Tabs.Pane>
    );
  }, [
    index,
    selectedFile,
    value,
    handleSetSelectedFile,
    fileName,
    handleSetFileName,
    aspectRatio,
    getImageCustomValidator,
    showBestCrop,
  ]);

  const renderModalActions = useCallback(
    (closeModal: () => void) => {
      closeModalRef.current = closeModal;
      return (
        <ImageUploaderModalButtons
          closeModal={() => closeModalWindow(closeModal)}
          handleLooksGood={handleLooksGood}
          isUploading={isUploading}
          canSubmit={!!croppedImage && (!!selectedFile || !!value)}
        />
      );
    },
    [closeModalWindow, handleLooksGood, isUploading, croppedImage, selectedFile, value],
  );

  const renderTabs = useCallback(() => {
    const panels = [renderUploadPane()];
    if (!isHidden) {
      panels.push(renderImageUploadSection());
    }
    return <Tabs>{panels}</Tabs>;
  }, [renderUploadPane, isHidden, renderImageUploadSection]);

  return (
    <Modal
      title="Select Image"
      renderTrigger={trigger}
      className="upload-image-modal"
      renderModalActions={renderModalActions}
      onCancel={() => closeModalWindow()}
      size={ModalSizes.large}
    >
      {renderTabs()}
    </Modal>
  );
};

export default ImageUploaderModal;
